import { DataSource } from "../../Data/DataSource";
import { PartialNullable } from "../../Extensions";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { Button } from "./Button";
import { IEntitySearch } from "./EntitySearch";
import { IEntitySearchInfiniteScroll } from "./EntitySearchInfiniteScroll";
import { Panel } from "./Panel";
import { ITableCellBase, TableCellBase, TableCellBasePropertyValues } from "./Table";
import { TableRowEntitySearch } from "./TableRowEntitySearch";
import { NAMESPACE } from "./_Namespace";

import "./TableCellEntityAction.scss";

export type TableCellEntityActionPropertyValues =
    TableCellBasePropertyValues
    & {
    };

export type TableCellEntityActionProperties = {
    [name in keyof TableCellEntityActionPropertyValues]: Property<TableCellEntityActionPropertyValues[name]>
};

export interface ITableCellEntityAction extends ITableCellBase, TableCellEntityActionProperties
{
}

export class TableCellEntityAction extends TableCellBase implements ITableCellEntityAction
{
    // #region Static

    public static override classNamespace = NAMESPACE + "TableCellEntityAction";
    public static override className = "TableCellEntityAction";

    // #endregion

    // #region Instance

    // #region Global Variables

    protected buttonEntityAction?: Button;
    protected buttonMoveDown?: Button;
    protected buttonMoveUp?: Button;
    protected panelMove?: Panel;
    protected panelSettings?: Panel;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellEntityActionPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-cell-entity-action");
    }

    // #endregion

    // #region Protected Methods

    protected _getEntityCommandInformation()
    {
        const
            entitySearch = this._getEntitySearch();

        if (!entitySearch)
            return null;

        const
            row = this._getRow(),
            rowDataSource = row ? row.dataSource.get() : null,
            record = rowDataSource ? rowDataSource.data.get() : null;

        if (!record)
            return null;

        const
            entityCommandInformation = entitySearch._getEntityCommandInformation();

        if (!entityCommandInformation)
            return null;

        entityCommandInformation.record.set(record);

        return entityCommandInformation;
    }
    protected _getEntitySearch(): IEntitySearch | IEntitySearchInfiniteScroll | undefined
    {
        const
            entitySearch = this.$$element.get()?.closest(".rad-control-entity-search").radControl() as IEntitySearch | undefined;

        if (entitySearch)
            return entitySearch;

        const
            entitySearchInfiniteScroll = this.$$element.get()?.closest(".rad-control-entity-search-infinite-scroll").radControl() as IEntitySearchInfiniteScroll | undefined;

        return entitySearchInfiniteScroll;
    }
    protected _getRow()
    {
        return this.$$element.get()?.closest(".rad-control-table-row").radControl() as TableRowEntitySearch | undefined;
    }
    protected _updateDisplay()
    {
        const
            entitySearch = this._getEntitySearch();

        let
            shouldDisplay = false;

        if (entitySearch)
            shouldDisplay = entitySearch._hasEntityCommandsForEntityCommandInformation(this._getEntityCommandInformation());

        if (shouldDisplay)
            this.removeCssClass("rad-state-no-entity-commands");
        else
            this.addCssClass("rad-state-no-entity-commands");

        if (entitySearch)
            entitySearch._updateTableColumnEntityAction();

        const
            entitySearchAsAny = (entitySearch as any);

        let
            entityNameFriendly;

        if (entitySearchAsAny?.applicationEntityNameFriendly)
            entityNameFriendly = (entitySearchAsAny as IEntitySearch).applicationEntityNameFriendly.get();
        else if (entitySearchAsAny?.entityNameFriendly)
            entityNameFriendly = (entitySearchAsAny as IEntitySearchInfiniteScroll).entityNameFriendly.get();

        this.buttonEntityAction?.elementTitle.set(entityNameFriendly ? `Open action menu for ${entityNameFriendly}` : null);
    }

    // #endregion

    // #region Overrides

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.panelSettings = new Panel({ cssClass: "rad-button-container-settings" }).appendTo(this);

        this.buttonEntityAction = new Button({ icon: "setting", inline: true, showLabel: false }).appendTo(this.panelSettings);
        this.buttonEntityAction.on("click", { self: this }, this.onButtonEntityActionClicked);

        this.panelMove = new Panel({ cssClass: "rad-button-container-move" }).appendTo(this);

        this.buttonMoveUp = new Button({ icon: "angle up", inline: true, showLabel: false }).appendTo(this.panelMove);
        this.buttonMoveUp.on("click", { self: this }, this.onButtonMoveUpClicked);

        this.buttonMoveDown = new Button({ icon: "angle down", inline: true, showLabel: false }).appendTo(this.panelMove);
        this.buttonMoveDown.on("click", { self: this }, this.onButtonMoveDownClicked);

        this._contentsElementRef.current.hide();

        return $$element;
    }
    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);
        this._updateDisplay();
    }
    protected override onAncestorChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        super.onAncestorChanged(evt, change)
        self._updateDisplay();
    }

    // #endregion

    // #region Event Handlers

    protected onButtonEntityActionClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            entitySearch = self._getEntitySearch();

        if (!entitySearch)
            return;

        const
            entityCommandInformation = self._getEntityCommandInformation();

        if (!entityCommandInformation)
            return;

        entitySearch.openEntityCommandMenuAsync(entityCommandInformation);
    }
    protected onButtonMoveDownClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            row = self._getRow(),
            entitySearch = self._getEntitySearch();

        if (!row)
            return;

        if (!entitySearch)
            return;

        if (!(entitySearch as any).rowMoveDown)
            return;

        (entitySearch as IEntitySearch).rowMoveDown(row);
    }
    protected onButtonMoveUpClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            row = self._getRow(),
            entitySearch = self._getEntitySearch();

        if (!row)
            return;

        if (!entitySearch)
            return;

        if (!(entitySearch as any).rowMoveUp)
            return;

        (entitySearch as IEntitySearch).rowMoveUp(row);
    }

    // #endregion

    // #endregion
}

